/**
 * Gera CSV de exemplo para campanha (telefone + colunas = chaves do template + metadados opcionais).
 * Separador `;` e BOM UTF-8 para abrir bem no Excel PT-BR; o backend aceita vírgula ou ponto e vírgula.
 *
 * Colunas de metadado (cnpj, razao_social, acessos, LINHA 1…3) alimentam os comandos
 * /cliente e /linhas no Chatwoot mesmo quando não são variáveis do template.
 */

export interface BuildExampleCsvOptions {
  phoneColumn?: string;
  templateKeys: string[];
  /** Valores da Zenvia `examples` por chave, quando existirem */
  examples?: Record<string, string>;
}

const OPTIONAL_METADATA_COLUMNS: { key: string; sample: string }[] = [
  { key: 'cnpj', sample: '00000000000100' },
  { key: 'razao_social', sample: 'Empresa Exemplo' },
  { key: 'administrador', sample: 'João Silva' },
  { key: 'email_adm', sample: 'adm@exemplo.com' },
  { key: 'telefone_adm', sample: '5541988887777' },
  { key: 'acessos', sample: '5' },
  { key: 'LINHA 1', sample: '5541999991111' },
  { key: 'LINHA 2', sample: '5541999992222' },
  { key: 'LINHA 3', sample: '5541999993333' },
];

function escapeCsvCell(value: string, separator: string): string {
  const needsQuote =
    value.includes('"') ||
    value.includes(separator) ||
    value.includes('\n') ||
    value.includes('\r');
  if (!needsQuote) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

function buildRow(cells: string[], separator: string): string {
  return cells.map((c) => escapeCsvCell(c, separator)).join(separator);
}

export function buildExampleCsvString(opts: BuildExampleCsvOptions): string {
  const separator = ';';
  const phoneCol = (opts.phoneColumn ?? 'phone').trim() || 'phone';
  const keys = opts.templateKeys ?? [];
  const header = [phoneCol, ...keys];

  const ex = opts.examples ?? {};
  const samplePhone = '5541999999999';
  const row1: string[] = [samplePhone];
  for (const k of keys) {
    const v = ex[k];
    row1.push(v != null && String(v).trim() !== '' ? String(v).trim() : '(preencher)');
  }

  const existing = new Set(header.map((h) => h.toLowerCase().trim()));
  for (const col of OPTIONAL_METADATA_COLUMNS) {
    if (!existing.has(col.key.toLowerCase().trim())) {
      header.push(col.key);
      row1.push(col.sample);
      existing.add(col.key.toLowerCase().trim());
    }
  }

  const lines = [buildRow(header, separator), buildRow(row1, separator)];
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

export function readTemplateExamples(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const ex = (raw as Record<string, unknown>).examples;
  if (!ex || typeof ex !== 'object' || Array.isArray(ex)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(ex)) {
    if (typeof v === 'string' && v.trim()) out[k] = v.trim();
  }
  return Object.keys(out).length ? out : undefined;
}

export function downloadExampleCsv(filenameBase: string, opts: BuildExampleCsvOptions): void {
  const content = buildExampleCsvString(opts);
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = filenameBase.replace(/[^\w\-]+/g, '_').slice(0, 80) || 'campanha';
  a.download = `${safe}_exemplo.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
